import type { Howl } from 'howler';
import { computed, ref } from 'vue';
import { Howl as HowlConstructor, Howler } from 'howler';
import registerSoundEventListener from '../utils/registerSoundsEventListener';
import { sleep } from '@frontend/special-utils';
import { FEATURES } from '../../../../config/features';

const FADE_DURATION = 500;

const getProjectLabel = () => 'akinfeev';

const isSoundLoaded = (sound: Howl | undefined): boolean => sound?.state() === 'loaded';

const getSounds = (name: string): string[] => [
  `https://sirena-cloud.sports.ru/${getProjectLabel()}/sounds/${name}/${name}.webm`,
  `https://sirena-cloud.sports.ru/${getProjectLabel()}/sounds/${name}/${name}.mp3`,
];

const AUDIO_MAP = {
  HOVER: 'hover',
  MAIN: 'main',
  CLICK: 'click',
};

const SOUNDS_CONFIG = {

  [AUDIO_MAP.CLICK]: {
    volume: 0.4,
  },
  [AUDIO_MAP.HOVER]: {
    volume: 0.2,
  },
};

const MUSICS_CONFIG = {
  [AUDIO_MAP.MAIN]: {
    volume: 0.5,
  },
};

const isAudioMenuOpened = ref(false);
const isSoundOnApp = ref(true);

const sounds = ref<Record<string, Howl> | null>(null);
const musics = ref<Record<string, Howl> | null>(null);
const currentMusic = ref<Howl | null>(null);
const isInitialized = ref(false);
const isFirstClick = ref(false);

// Computed свойства для проверки загрузки
const isSoundsLoaded = computed(() => {
  if (!sounds.value) return false;
  return Object.values(sounds.value).every(sound => isSoundLoaded(sound));
});

const isMusicLoaded = computed(() => {
  if (!musics.value) return false;
  return Object.values(musics.value).every(sound => isSoundLoaded(sound));
});

const isAllAudioLoaded = computed(() => isSoundsLoaded.value && isMusicLoaded.value);

export const useAudio = () => {
  const isAudioButtonVisible = computed(() => isAllAudioLoaded.value);
  const isAllAudioTurnedOff = computed(() => (isInitialized.value ? !isSoundOnApp.value : true));

  // Инициализация звуков
  const initSounds = async () => {
    if (!FEATURES.audio) return;

    if (isInitialized.value) return;

    try {
      const soundsEntries = Object.entries(SOUNDS_CONFIG);
      const musicsEntries = Object.entries(MUSICS_CONFIG);

      // Загружаем звуки
      const soundList = await Promise.all(
        soundsEntries.map(([key, value]) => new Promise<[string, Howl]>((resolve, reject) => {
          const sound = new HowlConstructor({
            src: getSounds(key),
            ...value,
            onload: () => resolve([key, sound]),
            onloaderror: (_id, error) => {
              console.error(`Failed to load sound ${key}:`, error);
              reject(error);
            },
          });
        })),
      );

      sounds.value = Object.fromEntries(soundList);

      // Загружаем музыку
      const musicList = await Promise.all(
        musicsEntries.map(([key, value]) => new Promise<[string, Howl]>((resolve, reject) => {
          const sound = new HowlConstructor({
            src: getSounds(key),
            loop: true,
            ...value,
            onload: () => resolve([key, sound]),
            onloaderror: (_id, error) => {
              console.error(`Failed to load music ${key}:`, error);
              reject(error);
            },
          });
        })),
      );

      musics.value = Object.fromEntries(musicList);

      // Регистрируем слушатель событий окна
      registerSoundEventListener((windowHidden: boolean) => {
        Howler.mute(windowHidden);
      });

      isInitialized.value = true;
      console.log('Audio initialized successfully');
    }
    catch (error) {
      console.error('Failed to initialize audio:', error);
    }
  };

  // Безопасное воспроизведение звука
  const playSound = (name: string) => {
    if (!isAllAudioLoaded.value || !isSoundOnApp.value) return;

    try {
      const sound = sounds.value?.[name];
      if (!sound || !isSoundLoaded(sound)) return;

      sound.play();
    }
    catch (error) {
      console.error(`Error playing sound ${name}:`, error);
    }
  };

  // Управление музыкой
  const muteMainMusic = async () => {
    if (!currentMusic.value) return;

    const currentVolume = MUSICS_CONFIG[AUDIO_MAP.MAIN]?.volume || 0.1;
    currentMusic.value.fade(currentVolume, 0, FADE_DURATION);
    await sleep(FADE_DURATION);
  };

  const unmuteMainMusic = () => {
    if (!currentMusic.value) return;

    const targetVolume = MUSICS_CONFIG[AUDIO_MAP.MAIN]?.volume || 0.1;
    currentMusic.value.fade(0, targetVolume, FADE_DURATION);
  };

  const stopMainMusic = async () => {
    const music = currentMusic.value;
    if (!music) return;

    await muteMainMusic();
    music.stop();
    currentMusic.value = null;
  };

  const makeDefaultMusic = () => {
    if (!isAllAudioLoaded.value || !isSoundOnApp.value || !musics.value) return;

    stopMainMusic();

    const music = musics.value[AUDIO_MAP.MAIN];
    currentMusic.value = music ?? null;

    if (!isSoundLoaded(music)) {
      console.error('Music is not loaded:', music);
      return;
    }

    console.log('Playing default music');
    music!.play();
  };

  const playMainSound = () => {
    if (!isAllAudioLoaded.value) {
      console.warn('Audio not loaded yet');
      return;
    }

    if (currentMusic.value) {
      unmuteMainMusic();
      return;
    }

    if (!isSoundOnApp.value) {
      console.warn('Sound is turned off');
      return;
    }

    makeDefaultMusic();
  };

  // Обработка первого клика
  const handleFirstClick = () => {
    if (!isFirstClick.value && isAllAudioLoaded.value) {
      isFirstClick.value = true;
      playMainSound();
    }
  };

  const makeClickSound = () => playSound(AUDIO_MAP.CLICK);
  const makeHoverSound = () => playSound(AUDIO_MAP.HOVER);

  const setAudioMenuState = (state: boolean) => {
    isAudioMenuOpened.value = state;
  };

  const toggleSoundState = () => {
    if (!isAllAudioLoaded.value) return false;
    if (!isInitialized.value) return false;

    isSoundOnApp.value = !isSoundOnApp.value;
    return isSoundOnApp.value ? playMainSound() : muteMainMusic();
  };

  return {
    isSoundsLoaded,
    isMusicLoaded,
    isAllAudioLoaded,
    isAudioMenuOpened,
    isAudioButtonVisible,
    isAllAudioTurnedOff,
    isInitialized,
    initSounds,
    handleFirstClick,
    playMainSound,
    setAudioMenuState,
    toggleSoundState,
    makeClickSound,
    makeHoverSound,
  };
};
